import { useState, useEffect } from "react";
import { getAllBilans } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  TrendingUp,
  Search,
  Euro,
  UtensilsCrossed,
  Calendar,
  Truck,
  MapPin,
  FileText,
  Loader2,
  BarChart3,
  Send,
  Plus,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BilansPage() {
  const [bilans, setBilans] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Manual bilan dialog
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [manualBilans, setManualBilans] = useState([]);
  const [sending, setSending] = useState(false);
  const [manualForm, setManualForm] = useState({
    food_truck_id: "",
    event_title: "",
    event_date: "",
    event_lieu: ""
  });

  useEffect(() => {
    loadBilans();
    loadFoodTrucks();
    loadManualBilans();
  }, []);

  const loadBilans = async () => {
    try {
      const response = await getAllBilans();
      setBilans(response.data.bilans);
      setTotaux(response.data.totaux);
    } catch (error) {
      toast.error("Erreur lors du chargement des bilans");
    } finally {
      setLoading(false);
    }
  };

  const loadFoodTrucks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/food-trucks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFoodTrucks(response.data.filter(ft => ft.statut === "actif"));
    } catch (error) {
      console.error("Error loading food trucks:", error);
    }
  };

  const loadManualBilans = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/bilans/manual-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManualBilans(response.data);
    } catch (error) {
      console.error("Error loading manual bilans:", error);
    }
  };

  const handleSendManualBilan = async () => {
    if (!manualForm.food_truck_id || !manualForm.event_title || !manualForm.event_date) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/bilans/send-manual`, manualForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Demande de bilan envoyée !");
      setShowManualDialog(false);
      setManualForm({ food_truck_id: "", event_title: "", event_date: "", event_lieu: "" });
      loadManualBilans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const filteredBilans = bilans.filter(b => 
    b.food_truck_nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.event_titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.event_lieu?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bilans-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary flex items-center gap-3">
            <BarChart3 className="w-8 h-8" />
            Bilans des Prestations
          </h1>
          <p className="text-muted-foreground mt-1">
            Résultats des événements remontés par les food trucks
          </p>
        </div>
        
        {/* Bouton envoi manuel */}
        <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="send-manual-bilan-btn">
              <Plus className="w-4 h-4 mr-2" />
              Demander un bilan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Demander un bilan de prestation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                Envoyez une demande de bilan pour un événement externe (non créé dans l'application).
              </p>
              
              <div className="space-y-2">
                <Label>Food Truck *</Label>
                <Select 
                  value={manualForm.food_truck_id} 
                  onValueChange={(v) => setManualForm({...manualForm, food_truck_id: v})}
                >
                  <SelectTrigger data-testid="select-food-truck">
                    <SelectValue placeholder="Sélectionner un food truck" />
                  </SelectTrigger>
                  <SelectContent>
                    {foodTrucks.map(ft => (
                      <SelectItem key={ft.id} value={ft.id}>
                        {ft.nom_food_truck}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Nom de l'événement *</Label>
                <Input 
                  placeholder="Ex: Festival de la Gastronomie"
                  value={manualForm.event_title}
                  onChange={(e) => setManualForm({...manualForm, event_title: e.target.value})}
                  data-testid="event-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date de l'événement *</Label>
                <Input 
                  type="date"
                  value={manualForm.event_date}
                  onChange={(e) => setManualForm({...manualForm, event_date: e.target.value})}
                  data-testid="event-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Lieu</Label>
                <Input 
                  placeholder="Ex: Paris, Place de la République"
                  value={manualForm.event_lieu}
                  onChange={(e) => setManualForm({...manualForm, event_lieu: e.target.value})}
                  data-testid="event-lieu-input"
                />
              </div>
              
              <Button 
                className="w-full" 
                onClick={handleSendManualBilan}
                disabled={sending}
                data-testid="confirm-send-bilan-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer la demande
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats totaux */}
      {totaux && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{totaux.nombre_bilans}</p>
                <p className="text-sm text-muted-foreground">Bilans reçus</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-accent/10 rounded-lg">
                <UtensilsCrossed className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{totaux.total_plats.toLocaleString('fr-FR')}</p>
                <p className="text-sm text-muted-foreground">Plats vendus</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-status-success/10 rounded-lg">
                <Euro className="w-6 h-6 text-status-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{totaux.total_ca_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                <p className="text-sm text-muted-foreground">CA HT Total</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{totaux.total_ca_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                <p className="text-sm text-muted-foreground">CA TTC Total</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recherche */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher par food truck, événement ou lieu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#E5E0DC]"
              data-testid="search-bilans"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tableau des bilans */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-secondary">
            Détail des bilans ({filteredBilans.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBilans.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-secondary">Aucun bilan</h3>
              <p className="text-muted-foreground">
                {searchTerm ? "Aucun résultat pour cette recherche" : "Les bilans des food trucks apparaîtront ici"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E5E0DC]">
                    <TableHead>Food Truck</TableHead>
                    <TableHead>Événement</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Lieu</TableHead>
                    <TableHead className="text-right">Plats</TableHead>
                    <TableHead className="text-right">CA HT</TableHead>
                    <TableHead className="text-right">CA TTC</TableHead>
                    <TableHead>Commentaire</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBilans.map((bilan, idx) => (
                    <TableRow key={`${bilan.event_id}-${bilan.food_truck_id}-${idx}`} className="border-[#E5E0DC]">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-primary" />
                          <span className="font-medium">{bilan.food_truck_nom}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-secondary">{bilan.event_titre}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {bilan.event_date && format(parseISO(bilan.event_date), "dd/MM/yyyy", { locale: fr })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {bilan.event_lieu || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="border-accent text-accent">
                          {bilan.nombre_plats}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-status-success">
                        {bilan.ca_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </TableCell>
                      <TableCell className="text-right font-bold text-primary">
                        {bilan.ca_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </TableCell>
                      <TableCell>
                        {bilan.commentaire ? (
                          <span className="text-sm text-muted-foreground italic max-w-[420px] whitespace-pre-wrap break-words block" title={bilan.commentaire}>
                            "{bilan.commentaire}"
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bilans manuels envoyés */}
      {manualBilans.length > 0 && (
        <Card className="bg-white border-[#E5E0DC]">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <Send className="w-5 h-5" />
              Demandes de bilans envoyées ({manualBilans.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Événement</TableHead>
                    <TableHead>Food Truck</TableHead>
                    <TableHead>Date événement</TableHead>
                    <TableHead>Envoyé le</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manualBilans.map((bilan) => (
                    <TableRow key={bilan.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{bilan.event_title}</p>
                          {bilan.event_lieu && (
                            <p className="text-sm text-muted-foreground">{bilan.event_lieu}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{bilan.food_truck_nom}</TableCell>
                      <TableCell>{bilan.event_date}</TableCell>
                      <TableCell>
                        {bilan.created_at ? format(parseISO(bilan.created_at), "dd/MM/yyyy HH:mm", { locale: fr }) : "-"}
                      </TableCell>
                      <TableCell>
                        {bilan.status === "submitted" ? (
                          <Badge className="bg-green-100 text-green-800">
                            Rempli
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800">
                            En attente
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
